'use client';

import Link from 'next/link';
import { useTranslations, useLocale } from 'next-intl';
import { motion } from 'framer-motion';
import { ArrowRight, Home, Mail, SearchX } from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';

export default function NotFound() {
  const t = useTranslations('notFound');
  const locale = useLocale();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen flex items-center justify-center px-4 py-20" style={{ background: 'linear-gradient(to bottom right, #f9fafb, #f3f4f6)' }}>
        <motion.div
          className="text-center max-w-2xl mx-auto"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Decorative Icon */}
          <motion.div variants={itemVariants} className="mb-8">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-white" style={{ boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' }}>
              <SearchX className="w-12 h-12" style={{ color: '#1F453B' }} />
            </div>
          </motion.div>

          {/* 404 Number */}
          <motion.div variants={itemVariants}>
            <div
              className="text-8xl md:text-9xl font-bold mb-4"
              style={{
                color: 'rgba(31, 69, 59, 0.1)',
                fontFamily: 'var(--font-montserrat)'
              }}
            >
              {t('heading')}
            </div>
          </motion.div>

          {/* Subtitle */}
          <motion.h1
            variants={itemVariants}
            className="text-3xl md:text-4xl font-bold mb-4"
            style={{
              color: '#111827',
              fontFamily: 'var(--font-montserrat)'
            }}
          >
            {t('subtitle')}
          </motion.h1>

          {/* Description */}
          <motion.p
            variants={itemVariants}
            className="text-lg mb-8"
            style={{
              color: '#4b5563',
              lineHeight: '1.75rem'
            }}
          >
            {t('description')}
          </motion.p>

          {/* Decorative Line */}
          <motion.div
            variants={itemVariants}
            className="w-16 h-1 mx-auto mb-12"
            style={{
              background: 'linear-gradient(to right, #1F453B, #D4A30B)',
            }}
          />

          {/* Action Buttons */}
          <motion.div
            variants={itemVariants}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link
              href={`/${locale}`}
              className="not-found-btn-primary inline-flex items-center justify-center px-8 py-3 text-white font-semibold rounded-lg transition-all duration-300 group"
            >
              <Home className="w-5 h-5 mr-2" />
              {t('backHome')}
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Link>

            <Link
              href={`/${locale}/contact`}
              className="not-found-btn-secondary inline-flex items-center justify-center px-8 py-3 border-2 font-semibold rounded-lg transition-all duration-300 bg-white"
            >
              <Mail className="w-5 h-5 mr-2" />
              {t('contactUs')}
            </Link>
          </motion.div>
        </motion.div>
      </div>
      <Footer />
    </>
  );
}
